import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import queryString from 'query-string'
import {
    Button,
    Checkbox,
    Breadcrumb,
    MessageBox,
    Message,
    Pagination,
} from 'element-react'
import CustomLoader from '../../components/user/includes/CustomLoader'

import {
    sendInvite,
    sendSelfActivationInvite,
} from '../../store/actions/actions'
import { Section, MainSection } from '../../components/views/includes/Wrappers'
import FormMessage from '../../components/user/includes/FormMessage'
import Spacer from '../../components/views/includes/Spacer'
import {
    tagHotjarRecording,
    isActive,
    singleLineAddress,
    wasInvited,
} from '../../helpers/util'
import {
    BlueBulletText,
    GreenBulletText,
    RedBulletText,
} from '../../helpers/BulletText'
import { useState } from 'react'
import useAdmin from '../../hooks/useAdmin'
import useAuth from '../../hooks/useAuth'
import AppRoutes from '../../routes/appRoutes'

const mapCompanies = companies =>
    companies.map(company => (
        <li key={`row-${company.id}`} className="admin-user">
            <div className="row">
                <div className="columns medium-7">
                    <Link
                        to={AppRoutes.adminSearch(`?company=${company.id}`)}
                        className="tertiary-color font-600"
                    >
                        <h5 className="inline-block">{company.name}</h5>
                    </Link>
                    <span className="secondary-color">{` - ${company.sap_id}`}</span>
                    <div className="font-400 text-xsmall secondary-color">
                        {singleLineAddress(
                            company.shipping_street1,
                            company.shipping_city,
                            company.shipping_state,
                            company.shipping_zip,
                        )}
                    </div>
                </div>
                <div className="columns medium-5">
                    <div className="small-background">
                        <Link
                            to={AppRoutes.adminSearch(`?company=${company.id}`)}
                            className="inline-block tertiary-color font-600 small-button"
                        >
                            <Button
                                type="info"
                                size="small"
                                style={{
                                    paddingLeft: '16.62px',
                                    paddingRight: '16.62px',
                                }}
                            >
                                View Users
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </li>
    ))

const mapCompaniesAndUsers = (page, companies, users, mapUsers) => {
    const resultsPerPage = 5

    const firstElement = (page - 1) * resultsPerPage
    const companiesForThisPage = companies.slice(
        firstElement,
        firstElement + resultsPerPage,
    )
    const companyElements = mapCompanies(companiesForThisPage)

    const usersNeeded = resultsPerPage - companyElements.length
    const firstUserElement =
        firstElement >= companies.length ? firstElement - companies.length : 0
    const usersForThisPage = users.slice(
        firstUserElement,
        firstUserElement + usersNeeded,
    )
    const userElements = mapUsers(usersForThisPage)

    return companyElements.concat(userElements)
}

const SearchResultsPage = props => {
    const {
        setCurrentCompanySapId,
        updateUserManagementStatus,
        impersonateUser,
        adminSuccess,
        adminHasErrored,
        errors,
        adminIsLoading,
    } = useAdmin()

    const values = queryString.parse(props.location.search)

    const pageType = values?.company
        ? 'COMPANY_LISTING'
        : values.query
            ? 'SEARCH_RESULTS'
            : 'UNKNOWN'

    const query = values?.query
    const companyId = values?.company
    const [company, setCompany] = useState({})
    const [page, setPage] = useState(values?.page ? parseInt(values.page) : 1)
    const [status, setStatus] = useState('INIT')
    const [error, setError] = useState('')
    const [users, setUsers] = useState([])
    const [companies, setCompanies] = useState([])
    const [totalResults, setTotalResults] = useState(0)
    const [companyWasDeleted, setCompanyWasDeleted] = useState(false)
    const { handleForgotPassword, role } = useAuth()

    const {
        handleSearchUsersAndCompanies,
        handleGetUsersInfo,
        handleRefreshCompanyData,
        handleDeleteUser,
        handleDeleteCompany,
    } = useAdmin()

    const onSearchSuccess = ({ data: { users, companies } }) => {
        setStatus('SUCCESS')
        setUsers(users)
        setCompanies(companies)
        setTotalResults(users.length + companies.length)
    }

    const onLoadedUsers = ({ data: { users, company, pagination } }) => {
        setStatus('SUCCESS')
        setUsers(users)
        setCompany(company)
        setCompanyWasDeleted(users === 0)
        setPage(pagination?.current_page)
        setTotalResults(pagination?.total)
        setCurrentCompanySapId(company?.sap_id)
    }

    const onUserDeleted = userId => {
        const usersLength = totalResults - 1
        const filteredUsers = users.filter(user => user.id !== userId)
        setUsers(filteredUsers)
        setTotalResults(usersLength)
        setStatus('SUCCESS')
        if (usersLength == 0) {
            props.history.push('/admin')
        }
    }

    const onRefreshCompany = response => {
        setStatus('SUCCESS')
        Message({
            message: response.data.message,
            type: 'success',
            showClose: true,
            duration: 1200,
            customClass: 'el-message--success',
            iconClass: 'el-message__icon el-icon-circle-check',
        })
    }

    const onDeleteCompany = () => {
        setTimeout(() => {
            console.log('Wait 2 seconds to load the company list page')
            setUsers([])
            setTotalResults(0)
            setStatus('SUCCESS')
            props.history.push('/admin')
        }, 2000)
    }

    const onError = (error, defaultMessage) => {
        tagHotjarRecording('API Error')
        let message = defaultMessage
        if (error.response.data) {
            message = error.response.data.message
        }
        setError(message)
        setStatus('ERROR')
    }

    useEffect(() => {
        setStatus('LOADING')
        if (pageType === 'COMPANY_LISTING') {
            getCompanyInfo()
        } else if (pageType === 'SEARCH_RESULTS') {
            handleSearchUsersAndCompanies(
                query,
                onSearchSuccess,
                onError,
                'No users or companies were found.',
            )
        } else {
            setStatus('ERROR')
            setError(
                'There was an error on this page. Please go back to the last page you were on.',
            )
        }

        return () => {
            setCurrentCompanySapId(undefined)
        }
    }, [])

    useEffect(() => {
        if (adminSuccess && companyWasDeleted) {
            Message({
                message: 'Update Successful',
                type: 'success',
                customClass: 'el-message--success',
                iconClass: 'el-message__icon el-icon-circle-check',
            })
        }

        if (status === 'ERROR') {
            Message({
                message: error ? error : 'An error occurred',
                type: 'error',
                showClose: true,
                duration: 1200,
                customClass: 'el-message--error',
                iconClass: 'el-message__icon el-icon-circle-cross',
            })
        }

        if (adminHasErrored) {
            Message({
                message: errors?.length ? errors[0] : 'Exception occurred',
                type: 'error',
                showClose: true,
                duration: 1200,
                customClass: 'el-message--error',
                iconClass: 'el-message__icon el-icon-circle-cross',
            })
        }
    }, [adminSuccess, companyWasDeleted, status])

    const getCompanyInfo = () => {
        const defaultParams = {
            query: query,
            company: companyId,
            page: page,
        }
        handleGetUsersInfo(
            defaultParams,
            onLoadedUsers,
            onError,
            'Users not found.',
        )
    }

    const goToPage = page => {
        if (pageType === 'COMPANY_LISTING') {
            let target = AppRoutes.adminSearch(`?query=${query}&page=${page}`)
            if (company) {
                target = AppRoutes.adminSearch(
                    `?company=${companyId}&page=${page}`,
                )
            }
            props.history.push(target)
        } else {
            setPage(page)
        }
    }

    const toggleCheck = () => {
        // this.setState({})
    }

    const toggleManager = (userId, value) => {
        MessageBox.confirm(
            "Are you sure you want to change this user's status?",
            "Change User's Status?",
            {
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No',
                customClass: 'small-prompt',
            },
        )
            .then(() => {
                // Sends the request to change the manager status to the backend
                updateUserManagementStatus(userId)

                // Finds the user in the array and chenges the manager status to the value of the checkbox
                let allUsers = users
                const index = allUsers.findIndex(user => user.id === userId)
                allUsers[index].manager = value
                setUsers(allUsers)
            })
            .catch(() => {
                toggleCheck() //reset box states
            })
    }

    const refreshData = companyId => {
        setStatus('LOADING')
        const data = { companyId: companyId }

        handleRefreshCompanyData(
            data,
            onRefreshCompany,
            onError,
            "Couldn't refresh data.",
        )
    }

    const deleteUser = (userId, query) => {
        setStatus('LOADING')
        handleDeleteUser(
            { userId, query },
            onUserDeleted,
            onError,
            "Couldn't delete user.",
        )
    }

    const deleteUserPrompt = userId => {
        MessageBox.confirm(
            'Are you sure you want to delete this user?',
            'Delete User?',
            {
                showCancelButton: true,
                confirmButtonText: 'Yes, Delete',
                cancelButtonText: 'No',
                customClass: 'small-prompt',
            },
        )
            .then(() => {
                deleteUser(userId, query)
            })
            .catch(() => {
                console.error('There was an error during user deletion.')
            })
    }

    const deleteFullCompany = (companyId, query) => {
        setStatus('LOADING')
        handleDeleteCompany(
            { companyId, query },
            onDeleteCompany,
            onError,
            "Couldn't delete company.",
        )
    }

    const deleteCompanyPrompt = companyId => {
        // Companies are called Customers in the UI, but are usually called
        // companies in the code.
        MessageBox.confirm(
            'Are you sure you want to delete this customer? All associated users and data will be deleted.',
            'Delete Customer?',
            {
                showCancelButton: true,
                confirmButtonText: 'Yes, Delete',
                cancelButtonText: 'No',
                customClass: 'small-prompt',
            },
        ).then(() => {
            deleteFullCompany(companyId, query)
        })
    }

    const resetPassword = email => {
        handleForgotPassword(email)
    }

    const sendActivationMail = userId => {
        sendSelfActivationInvite(userId).then(() =>
            createInvite(userId, 'activation'),
        )
    }

    const sendInvitationMail = userId => {
        sendInvite(userId).then(() => createInvite(userId, 'invite'))
    }

    const createInvite = (userId, type) => {
        let invite = { user_id: userId, type: type }
        const allUsers = users?.map(item => {
            if (item?.id === userId) {
                return {
                    ...item,
                    invites:
                        item?.invites?.length === 0 ? [invite] : item?.invites,
                }
            }
            return item
        })
        setUsers(allUsers)
    }

    const deleteCompanyButton =
        role !== 'admin' ? null : (
            <Button
                onClick={() => deleteCompanyPrompt(company.id)}
                type="danger"
                size="small"
            >
                Delete
            </Button>
        )

    const isUserListingForCompany = pageType === 'COMPANY_LISTING'
    const header = companyWasDeleted ? (
        <FormMessage
            messageTitle="Customer Not Found"
            messageContent="This customer has been removed from LiftOne Now."
            buttonLink={AppRoutes.adminHome()}
            buttonLabel="Customer List lalal"
        />
    ) : isUserListingForCompany ? (
        <div className="row">
            <div className="small-7 columns">
                {/* Company Name */}
                <h4 className="margin-bottom-none">{company?.name}</h4>
            </div>
            <div className="small-5 columns">
                <div className="row">
                    <div className="columns medium-6 small-12 text-right hide-for-medium">
                        {deleteCompanyButton}
                    </div>
                    <div className="columns medium-6 small-12 text-right padding-collapse show-for-medium">
                        {deleteCompanyButton}
                    </div>
                    <div className="columns medium-6 small-12 text-right">
                        <Button
                            type="text"
                            onClick={() => refreshData(company?.external_id)}
                            className="manual-refresh button-icon"
                        >
                            <i className="icon-refresh"></i> Manual Refresh
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    ) : (
        <Section>
            <h4 className="secondary-color">
                Search Results:{' '}
                <span className="font-600 tertiary-color">‘{query}’</span>{' '}
            </h4>
        </Section>
    )

    const isAdmin = role === 'admin'
    const sendInviteButton = user =>
        isActive(user) ? null : (
            <Button
                type="info"
                size="small"
                style={{ paddingLeft: '6.7px', paddingRight: '6.7px' }}
                onClick={() => sendInvitationMail(user.id)}
                icon="message"
            >
                {' '}
                Send Invite
            </Button>
        )
    const activationButton = user =>
        isActive(user) || !isAdmin ? null : (
            <Button
                type="info"
                size="small"
                style={{ paddingLeft: '8.8px', paddingRight: '8.8px' }}
                onClick={() => sendActivationMail(user.id)}
                icon="message"
            >
                {' '}
                Activation
            </Button>
        )
    const logInAsButton = user => (
        <Button
            type="text"
            className="button-icon"
            onClick={() => impersonateUser(user.id, role)}
        >
            <i className="icon-login-as" />
            Log In As
        </Button>
    )
    const deleteUserButton = user =>
        !isAdmin ? null : (
            <Button
                onClick={() => deleteUserPrompt(user.id)}
                type="danger"
                icon="delete"
                size="small"
                style={{
                    /* To match height of Send Invite button */
                    lineHeight: '13px',
                }}
            >
                {' '}
                Remove
            </Button>
        )

    const mapUsers = users =>
        users.map(user => (
            <li className="admin-user" key={`row-${user.id}`}>
                <div className="row">
                    <div className="columns medium-7 position-relative">
                        <div className="content">
                            {/* Name */}
                            <h5 className="inline-block">
                                {user.first_name} {user.last_name}
                            </h5>
                            {isActive(user) ? (
                                <GreenBulletText text="Activated" />
                            ) : wasInvited(user) ? (
                                <BlueBulletText text="Invited" />
                            ) : (
                                <RedBulletText text="Not Invited" />
                            )}
                            {query && user?.company ? (
                                <Link
                                    to={AppRoutes.adminSearch(
                                        `?company=${user?.company?.id}`,
                                    )}
                                    className="underline secondary-color"
                                >
                                    <small>{user?.company?.name}</small>
                                </Link>
                            ) : (
                                ''
                            )}

                            {/* Email */}
                            <span className="secondary-color block truncate">
                                {user?.email}
                            </span>

                            <Checkbox
                                checked={user?.manager}
                                onChange={value =>
                                    toggleManager(user.id, value)
                                }
                            >
                                Customer Account Manager
                            </Checkbox>

                            <Spacer size="tiny" />

                            {/* Reset Password */}
                            {!isActive(user) || !isAdmin ? null : (
                                <Button
                                    type="default"
                                    onClick={() => resetPassword(user?.email)}
                                >
                                    Force Password Reset
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="columns medium-5">
                        <div className={isAdmin ? 'flow-grid' : 'flow-grid-2'}>
                            <div className="flow-grid__item">
                                <div className="align-bottom center-button">
                                    {sendInviteButton(user)}
                                </div>
                            </div>
                            {isAdmin ? (
                                <div className="flow-grid__item center-button">
                                    <div className="align-bottom">
                                        {activationButton(user)}
                                    </div>
                                </div>
                            ) : null}
                            <div className="flow-grid__item">
                                <div className="align-bottom">
                                    {logInAsButton(user)}
                                </div>
                            </div>
                            {isAdmin ? (
                                <div className="flow-grid__item">
                                    <div className="align-bottom">
                                        {deleteUserButton(user)}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </li>
        ))

    const areNoResults =
        (!Array.isArray(companies) || !companies.length) &&
        (!Array.isArray(users) || !users.length)
    const listings = companyWasDeleted ? null : areNoResults ? (
        <div>No results found. Please try again.</div>
    ) : pageType === 'COMPANY_LISTING' ? (
        <ul className="table-list">{mapUsers(users)}</ul>
    ) : pageType === 'SEARCH_RESULTS' ? (
        <ul className="table-list">
            {mapCompaniesAndUsers(page, companies, users, mapUsers)}
        </ul>
    ) : null

    const areMoreThatFiveResults = totalResults > 5
    const pagination = companyWasDeleted ? null : areMoreThatFiveResults ? (
        <div className="row">
            <div className="columns large-6 medium-6 small-12">
                <Pagination
                    layout="prev, pager, next"
                    onCurrentChange={pg => goToPage(pg)}
                    pageSize={5}
                    currentPage={page}
                    total={totalResults}
                    small={true}
                />
            </div>
            <div className="columns large-6 medium-6 small-12">
                <div className="text-right">
                    <small>{totalResults} Results Found </small>
                </div>
            </div>
        </div>
    ) : (
        <Section>
            <div className="medium-text-right">
                <small>
                    {totalResults} Result
                    {totalResults != 1 ? 's' : ''} Found{' '}
                </small>
            </div>
        </Section>
    )

    if (status === 'LOADING' || adminIsLoading) {
        return (
            <div className="demo-loading padding-top-large padding-bottom-large">
                {' '}
                <CustomLoader text="Loading"> </CustomLoader>
            </div>
        )
    }

    return (
        <MainSection className="admin--user-management">
            {status === 'LOADING' && (
                <Section>
                    <CustomLoader text="Loading" />
                </Section>
            )}
            <div className="row">
                <div className="columns medium-9">
                    <Breadcrumb separator="/" className="margin-top-small">
                        <Breadcrumb.Item>
                            <Link to={AppRoutes.home()}>Home</Link>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            {company && company?.name
                                ? company.name
                                : 'Search: ' + query}
                        </Breadcrumb.Item>
                    </Breadcrumb>
                </div>
                <div className="columns medium-3 show-for-medium">
                    <Button
                        type="text"
                        onClick={() => props.history.goBack()}
                        className="button-back margin-top-small"
                    >
                        <i className="icon-chevron-thin-left"></i> Go Back
                    </Button>
                </div>
            </div>

            <Spacer size="small" />

            {header}

            <Section>{listings}</Section>

            {pagination}
        </MainSection>
    )
}

SearchResultsPage.propTypes = {
    history: PropTypes.shape({
        goBack: PropTypes.func.isRequired,
        push: PropTypes.func.isRequired,
    }),
    location: PropTypes.shape({
        search: PropTypes.string,
    }),
}

export default SearchResultsPage
